// Custom Hook for Concierge Chat State Management

import { useState, useCallback, useRef, useEffect } from 'react';
import type { Place, Realm } from '../types';

// Simple UUID generator (crypto.randomUUID or fallback)
const generateId = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};
import type {
  ConciergeMessage,
  ConciergeConversation,
  ConciergeChatState,
} from '../types/concierge';
import {
  startConciergeChat,
  sendConciergeMessage,
  clearConciergeChat,
  hasConciergeChat,
} from '../services/conciergeService';

interface UseConciergeProps {
  places: Place[];
  realm: Realm;
  userId?: string;
  userName?: string;
}

interface UseConciergeReturn extends ConciergeChatState {
  initializeChat: () => Promise<void>;
  sendMessage: (text: string) => Promise<void>;
  resetChat: () => void;
}

export function useConcierge({
  places,
  realm,
  userId,
  userName,
}: UseConciergeProps): UseConciergeReturn {
  const [state, setState] = useState<ConciergeChatState>({
    isLoading: false,
    error: null,
    conversation: null,
    isTyping: false,
  });

  const conversationIdRef = useRef<string>(generateId());
  const isInitializedRef = useRef(false);

  // Initialize chat session
  const initializeChat = useCallback(async () => {
    // Skip if already initialized or no places
    if (isInitializedRef.current && hasConciergeChat(conversationIdRef.current)) {
      return;
    }

    if (places.length === 0) {
      setState(prev => ({
        ...prev,
        error: 'Keine Orte verfuegbar',
      }));
      return;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const success = await startConciergeChat(
        conversationIdRef.current,
        places,
        realm,
        userName
      );

      if (success) {
        isInitializedRef.current = true;
        setState(prev => ({
          ...prev,
          isLoading: false,
          conversation: {
            id: conversationIdRef.current,
            userId: userId || 'anonymous',
            messages: [
              {
                id: generateId(),
                role: 'assistant',
                content: 'Wie kann ich dir bei der Suche helfen?',
                timestamp: new Date(),
              },
            ],
            createdAt: new Date(),
            updatedAt: new Date(),
            activeRealm: realm,
          },
        }));
      } else {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: 'Chat konnte nicht gestartet werden',
        }));
      }
    } catch (error) {
      console.error('Failed to initialize concierge:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Fehler beim Starten des Chats',
      }));
    }
  }, [places, realm, userId, userName]);

  // Send message
  const sendMessage = useCallback(
    async (text: string) => {
      if (!state.conversation || state.isTyping || !text.trim()) return;

      // Add user message immediately
      const userMessage: ConciergeMessage = {
        id: generateId(),
        role: 'user',
        content: text.trim(),
        timestamp: new Date(),
      };

      setState(prev => ({
        ...prev,
        isTyping: true,
        error: null,
        conversation: prev.conversation
          ? {
              ...prev.conversation,
              messages: [...prev.conversation.messages, userMessage],
            }
          : null,
      }));

      // Get AI response
      try {
        const response = await sendConciergeMessage(
          conversationIdRef.current,
          text.trim()
        );

        if (response) {
          const assistantMessage: ConciergeMessage = {
            id: generateId(),
            role: 'assistant',
            content: response.message,
            timestamp: new Date(),
            placeRecommendations: response.places.length > 0 ? response.places : undefined,
          };

          setState(prev => ({
            ...prev,
            isTyping: false,
            conversation: prev.conversation
              ? {
                  ...prev.conversation,
                  messages: [...prev.conversation.messages, assistantMessage],
                  updatedAt: new Date(),
                }
              : null,
          }));
        } else {
          setState(prev => ({
            ...prev,
            isTyping: false,
            error: 'Keine Antwort erhalten. Bitte versuche es erneut.',
          }));
        }
      } catch (error) {
        console.error('Failed to send message:', error);
        setState(prev => ({
          ...prev,
          isTyping: false,
          error: 'Fehler beim Senden der Nachricht',
        }));
      }
    },
    [state.conversation, state.isTyping]
  );

  // Reset conversation
  const resetChat = useCallback(() => {
    clearConciergeChat(conversationIdRef.current);
    conversationIdRef.current = generateId();
    isInitializedRef.current = false;
    setState({
      isLoading: false,
      error: null,
      conversation: null,
      isTyping: false,
    });
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Optionally keep the session for resuming
      // clearConciergeChat(conversationIdRef.current);
    };
  }, []);

  return {
    ...state,
    initializeChat,
    sendMessage,
    resetChat,
  };
}
